import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Save, Plus, Trash2 } from 'lucide-react';
import { useSubsidieIntake, type IntakeTarget, type SubsidieIntakeData } from '@/hooks/useSubsidieIntake';

interface SubsidieIntakeFormProps {
  target: IntakeTarget;
  onSaved?: () => void;
}

export function SubsidieIntakeForm({ target, onSaved }: SubsidieIntakeFormProps) {
  const { intakeData, isLoading, volledigheid, saveIntake, isSaving } = useSubsidieIntake(target);
  const [activeTab, setActiveTab] = useState('bedrijf');
  const [formData, setFormData] = useState<SubsidieIntakeData>({});

  useEffect(() => {
    if (intakeData && Object.keys(intakeData).length > 0) {
      setFormData(intakeData);
    }
  }, [intakeData]);

  const update = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    await saveIntake(formData);
    onSaved?.();
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-8 text-muted-foreground">Laden...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header with progress */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Volledigheid</span>
          <Progress value={volledigheid} className="w-32 h-2" />
          <Badge variant={volledigheid >= 80 ? 'default' : volledigheid >= 40 ? 'secondary' : 'outline'}>
            {volledigheid}%
          </Badge>
        </div>
        <Button onClick={handleSave} disabled={isSaving} size="sm">
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Opslaan...' : 'Opslaan'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="bedrijf">Bedrijf</TabsTrigger>
          <TabsTrigger value="locatie">Locatie</TabsTrigger>
          <TabsTrigger value="net">Net</TabsTrigger>
          <TabsTrigger value="laadinfra">Laadinfra</TabsTrigger>
          <TabsTrigger value="batterij">Batterij</TabsTrigger>
          <TabsTrigger value="voertuigen">Voertuigen</TabsTrigger>
          <TabsTrigger value="investering">Investering</TabsTrigger>
        </TabsList>

        {/* Tab 1: Bedrijfsgegevens */}
        <TabsContent value="bedrijf">
          <Card>
            <CardHeader><CardTitle>Bedrijfsgegevens</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bedrijfsgrootte</Label>
                  <Select
                    value={formData.bedrijfsgrootte ?? ''}
                    onValueChange={v => update('bedrijfsgrootte', v)}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="klein">Klein (&lt;50 fte)</SelectItem>
                      <SelectItem value="middel">Middel (&lt;250 fte)</SelectItem>
                      <SelectItem value="mkb">MKB</SelectItem>
                      <SelectItem value="groot">Groot (&gt;250 fte)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Aantal FTE</Label>
                  <Input
                    type="number"
                    value={formData.fte ?? ''}
                    onChange={e => update('fte', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="bijv. 45"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sector</Label>
                  <Select
                    value={formData.sector ?? ''}
                    onValueChange={v => update('sector', v)}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transport">Transport & Logistiek</SelectItem>
                      <SelectItem value="bouw">Bouw</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="industrie">Industrie</SelectItem>
                      <SelectItem value="agrarisch">Agrarisch</SelectItem>
                      <SelectItem value="overheid">Overheid</SelectItem>
                      <SelectItem value="overig">Overig</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Postcode (2-cijferig)</Label>
                  <Input
                    value={formData.postcode_2cijferig ?? ''}
                    onChange={e => update('postcode_2cijferig', e.target.value)}
                    placeholder="bijv. 56"
                    maxLength={2}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Locatie */}
        <TabsContent value="locatie">
          <Card>
            <CardHeader><CardTitle>Locatiegegevens</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Publiek toegankelijk</Label>
                <Switch
                  checked={formData.locatie_publiek_toegankelijk ?? false}
                  onCheckedChange={v => update('locatie_publiek_toegankelijk', v)}
                />
              </div>
              <div className="space-y-2">
                <Label>Type locatie</Label>
                <Select
                  value={formData.locatie_type ?? ''}
                  onValueChange={v => update('locatie_type', v)}
                >
                  <SelectTrigger><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bedrijventerrein">Bedrijventerrein</SelectItem>
                    <SelectItem value="nabij_weg">Nabij A/N-weg (&lt;1 km)</SelectItem>
                    <SelectItem value="overig">Overig</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Afstand tot A/N-weg (km)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.afstand_an_weg_km ?? ''}
                  onChange={e => update('afstand_an_weg_km', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="bijv. 0.5"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Bereikbaar via verharde weg &ge;6m breed</Label>
                <Switch
                  checked={formData.bereikbaar_6m_verhard ?? false}
                  onCheckedChange={v => update('bereikbaar_6m_verhard', v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Toegankelijk 40+ uur/week (09:00-21:00)</Label>
                <Switch
                  checked={formData.toegankelijk_40u_week ?? false}
                  onCheckedChange={v => update('toegankelijk_40u_week', v)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3: Netaansluiting */}
        <TabsContent value="net">
          <Card>
            <CardHeader><CardTitle>Netaansluiting</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Netaansluiting (kVA)</Label>
                <Input
                  type="number"
                  value={formData.netaansluiting_kva ?? ''}
                  onChange={e => update('netaansluiting_kva', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="bijv. 630"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Grootverbruiker (&gt;3x80A)</Label>
                <Switch
                  checked={formData.grootverbruiker ?? false}
                  onCheckedChange={v => update('grootverbruiker', v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Netcongestie (afname) in postcodegebied</Label>
                <Switch
                  checked={formData.netcongestie_afname ?? false}
                  onCheckedChange={v => update('netcongestie_afname', v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Netbeheerder bevestigt &ge;100 kVA binnen 2 jaar</Label>
                <Switch
                  checked={formData.netbeheerder_bevestiging_2jaar ?? false}
                  onCheckedChange={v => update('netbeheerder_bevestiging_2jaar', v)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 4: Laadinfra */}
        <TabsContent value="laadinfra">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Laadinfrastructuur</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => update('laadstations', [
                    ...(formData.laadstations ?? []),
                    { type: 'DC', vermogen_kw: 150, aantal: 1, connector: 'CCS' }
                  ])}
                >
                  <Plus className="h-4 w-4 mr-1" /> Laadstation
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {(formData.laadstations ?? []).map((station, idx) => (
                <div key={idx} className="grid grid-cols-5 gap-2 items-end border rounded-lg p-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <Select
                      value={station.type}
                      onValueChange={v => {
                        const updated = [...(formData.laadstations ?? [])];
                        updated[idx] = { ...station, type: v as 'AC' | 'DC' };
                        update('laadstations', updated);
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AC">AC</SelectItem>
                        <SelectItem value="DC">DC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Vermogen (kW)</Label>
                    <Input
                      type="number"
                      value={station.vermogen_kw}
                      onChange={e => {
                        const updated = [...(formData.laadstations ?? [])];
                        updated[idx] = { ...station, vermogen_kw: Number(e.target.value) };
                        update('laadstations', updated);
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Aantal</Label>
                    <Input
                      type="number"
                      value={station.aantal}
                      onChange={e => {
                        const updated = [...(formData.laadstations ?? [])];
                        updated[idx] = { ...station, aantal: Number(e.target.value) };
                        update('laadstations', updated);
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Connector</Label>
                    <Select
                      value={station.connector ?? 'CCS'}
                      onValueChange={v => {
                        const updated = [...(formData.laadstations ?? [])];
                        updated[idx] = { ...station, connector: v };
                        update('laadstations', updated);
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CCS">CCS</SelectItem>
                        <SelectItem value="MCS">MCS</SelectItem>
                        <SelectItem value="Type2">Type 2</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const updated = (formData.laadstations ?? []).filter((_, i) => i !== idx);
                      update('laadstations', updated);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <Label>Totaal laadvermogen (kW)</Label>
                  <Input
                    type="number"
                    value={formData.totaal_laadvermogen_kw ?? ''}
                    onChange={e => update('totaal_laadvermogen_kw', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="Automatisch of handmatig"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Doelvoertuigen</Label>
                  <div className="flex flex-wrap gap-2">
                    {['M3', 'N2', 'N3', 'M1', 'N1'].map(cat => (
                      <Button
                        key={cat}
                        variant={(formData.doelvoertuigen ?? []).includes(cat) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          const current = formData.doelvoertuigen ?? [];
                          const updated = current.includes(cat)
                            ? current.filter(c => c !== cat)
                            : [...current, cat];
                          update('doelvoertuigen', updated);
                        }}
                      >
                        {cat}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 5: Batterij */}
        <TabsContent value="batterij">
          <Card>
            <CardHeader><CardTitle>Batterij & EMS</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Batterijopslag gepland</Label>
                <Switch
                  checked={formData.batterij_gepland ?? false}
                  onCheckedChange={v => update('batterij_gepland', v)}
                />
              </div>
              {formData.batterij_gepland && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Capaciteit (kWh)</Label>
                      <Input
                        type="number"
                        value={formData.batterij_kwh ?? ''}
                        onChange={e => update('batterij_kwh', e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="bijv. 800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Vermogen (kW)</Label>
                      <Input
                        type="number"
                        value={formData.batterij_kw ?? ''}
                        onChange={e => update('batterij_kw', e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="bijv. 400"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>&ge;70% voor laadinfrastructuur</Label>
                    <Switch
                      checked={formData.batterij_70pct_laadinfra ?? false}
                      onCheckedChange={v => update('batterij_70pct_laadinfra', v)}
                    />
                  </div>
                </>
              )}
              <div className="flex items-center justify-between">
                <Label>EMS gepland</Label>
                <Switch
                  checked={formData.ems_gepland ?? false}
                  onCheckedChange={v => update('ems_gepland', v)}
                />
              </div>
              <div className="space-y-2">
                <Label>PV vermogen (kWp)</Label>
                <Input
                  type="number"
                  value={formData.pv_kwp ?? ''}
                  onChange={e => update('pv_kwp', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="bijv. 760"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 6: Voertuigen */}
        <TabsContent value="voertuigen">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Zero-emissie voertuigen</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => update('trucks_gepland', [
                    ...(formData.trucks_gepland ?? []),
                    { categorie: 'N3', type: 'bakwagen', brandstof: 'E', verkoopprijs: 0 }
                  ])}
                >
                  <Plus className="h-4 w-4 mr-1" /> Voertuig
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {(formData.trucks_gepland ?? []).map((truck, idx) => (
                <div key={idx} className="grid grid-cols-5 gap-2 items-end border rounded-lg p-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Categorie</Label>
                    <Select
                      value={truck.categorie}
                      onValueChange={v => {
                        const updated = [...(formData.trucks_gepland ?? [])];
                        updated[idx] = { ...truck, categorie: v };
                        update('trucks_gepland', updated);
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="N2">N2 (&ge;10t)</SelectItem>
                        <SelectItem value="N3">N3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <Select
                      value={truck.type}
                      onValueChange={v => {
                        const updated = [...(formData.trucks_gepland ?? [])];
                        updated[idx] = { ...truck, type: v };
                        update('trucks_gepland', updated);
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bakwagen">Bakwagen</SelectItem>
                        <SelectItem value="trekker">Trekker</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Brandstof</Label>
                    <Select
                      value={truck.brandstof}
                      onValueChange={v => {
                        const updated = [...(formData.trucks_gepland ?? [])];
                        updated[idx] = { ...truck, brandstof: v };
                        update('trucks_gepland', updated);
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="E">Elektrisch</SelectItem>
                        <SelectItem value="W">Waterstof</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Chassisprijs excl. BTW</Label>
                    <Input
                      type="number"
                      value={truck.verkoopprijs ?? ''}
                      onChange={e => {
                        const updated = [...(formData.trucks_gepland ?? [])];
                        updated[idx] = { ...truck, verkoopprijs: Number(e.target.value) };
                        update('trucks_gepland', updated);
                      }}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const updated = (formData.trucks_gepland ?? []).filter((_, i) => i !== idx);
                      update('trucks_gepland', updated);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              {(formData.trucks_gepland ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground">Geen voertuigen toegevoegd. Klik "Voertuig" om er een toe te voegen.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 7: Investering */}
        <TabsContent value="investering">
          <Card>
            <CardHeader><CardTitle>Investeringsbedragen</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Totale investering</Label>
                  <Input
                    type="number"
                    value={formData.investering_totaal ?? ''}
                    onChange={e => update('investering_totaal', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Laadinfrastructuur</Label>
                  <Input
                    type="number"
                    value={formData.investering_laadinfra ?? ''}
                    onChange={e => update('investering_laadinfra', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Batterijopslag</Label>
                  <Input
                    type="number"
                    value={formData.investering_batterij ?? ''}
                    onChange={e => update('investering_batterij', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>EMS</Label>
                  <Input
                    type="number"
                    value={formData.investering_ems ?? ''}
                    onChange={e => update('investering_ems', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="0"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
